import css from "../../styles/pendo_override.css?inline";

const MARKER = "data-bridge-support-menu-pendo-override";

// US2003211: document-level dark-mode hook. The injected <style> lives in
// document.head and styles Pendo DOM in document.body — outside our shadow root —
// so dark rules cannot key off an attribute on our element. Instead the dark
// rules in pendo_override.css are scoped under
// html[data-bridge-support-menu-theme="dark"], and this module owns the attribute
// so it shares the <style> node's lifecycle (set on bb_theme=dark while injected;
// cleared on the 1→0 teardown). Analog to the US1990925 document.head injection
// exception — recorded as EXCEPTION (US2003211) in project-context.md.
const THEME_HOOK = "data-bridge-support-menu-theme";

// Module-level ref-count is the single source of truth for the shared <style>:
// N component instances share exactly ONE injected node, created on the 0->1
// transition and removed on the 1->0 transition. Without this, a teardown from
// the first instance to disconnect would strip the stylesheet out from under a
// still-mounted sibling (AC-3 idempotency and AC-4 teardown cannot both hold
// otherwise). The marker-query in each function is a defensive idempotency check
// only — it never drives the count, so the count stays honest about how many
// instances hold the node.
let refCount = 0;

// US2003211 (PR #109 review): the dark hook is INDEPENDENTLY ref-counted from the
// <style> node. The hook is shared global state on the single document.documentElement
// but is driven per-instance from bb_theme, so without a ref-count a light instance
// connecting after a dark one would remove the dark sibling's hook (last-writer-wins).
// This counts "how many live instances want dark" so the hook reflects ANY live dark
// request, not whoever wrote last. Each instance contributes exactly one unit while it
// holds a dark request (App.ts tracks its own _darkHooked to stay balanced across
// re-attach). The hook is set on every increment and removed only on the 1→0 dark
// transition — it no longer rides the <style> ref-count's teardown (own lifecycle).
let darkRefCount = 0;

export function injectPendoOverride(): void {
  // DOM-ownership check BEFORE the increment, so refCount only ever counts nodes
  // this module actually created. If the increment ran first, a marker node that
  // already exists at the 0->1 transition (HMR reload, a second bundle copy with
  // its own refCount, or an orphan from an unbalanced cycle) would push the count
  // to 1 with no appendChild — and a later 1->0 removePendoOverride() would then
  // delete a node this module never created, breaking the count's own invariant.
  if (refCount === 0 && !document.head.querySelector(`[${MARKER}]`)) {
    const style = document.createElement("style");
    style.setAttribute(MARKER, "");
    // textContent (NOT innerHTML) from the static ?inline import — no dynamic
    // data, no injection surface (Veracode NFR-S5).
    style.textContent = css;
    document.head.appendChild(style);
  }
  refCount += 1;
}

export function removePendoOverride(): void {
  if (refCount === 0) return; // underflow guard — never decrement below 0
  refCount -= 1;
  // Only the 1->0 transition removes the node. The dark hook is NO LONGER cleared
  // here — it owns its own ref-count (incrementDarkThemeHook/decrementDarkThemeHook)
  // and is cleared by the unmounting instance's decrement (App.ts disconnectedCallback),
  // so its set/clear ownership lives in one place (PR #109 review: lifecycle coupling).
  if (refCount === 0) {
    document.head.querySelector(`[${MARKER}]`)?.remove();
  }
}

/**
 * Adds one dark request to the document-level dark-mode hook
 * (`data-bridge-support-menu-theme="dark"` on `<html>`), which activates the dark
 * layer in `pendo_override.css` for the Pendo Resource Center container. Sets the
 * attribute on every call (idempotent: the attribute value never changes once present).
 *
 * NOTE: this hook no longer themes the Get Help button — that moved to the trigger's
 * own `:host([dark])` shadow CSS (driven by App.ts's `dark` property) so the button
 * themes in legacy mode too, where this Pendo-only stylesheet is never injected.
 *
 * CALLER CONTRACT: only call while `_pendoInjected === true`, and call exactly once
 * per instance that wants dark (App.ts gates on its `_darkHooked` flag so connect /
 * theme-change / re-attach stay balanced). Pair every increment with exactly one
 * {@link decrementDarkThemeHook}. Calling when no Pendo CSS is injected would leave a
 * stale hook on `<html>` activating dark rules for a stylesheet that isn't there.
 */
export function incrementDarkThemeHook(): void {
  darkRefCount += 1;
  document.documentElement.setAttribute(THEME_HOOK, "dark");
}

/**
 * Removes one dark request. The hook is cleared from `<html>` only on the 1→0
 * transition (the last live instance that wanted dark went away), so a light sibling
 * unmounting can never strip a still-mounted dark instance's hook. Underflow-guarded:
 * decrementing at 0 is a no-op. Pair with exactly one {@link incrementDarkThemeHook}.
 */
export function decrementDarkThemeHook(): void {
  if (darkRefCount === 0) return; // underflow guard — never decrement below 0
  darkRefCount -= 1;
  if (darkRefCount === 0) {
    document.documentElement.removeAttribute(THEME_HOOK);
  }
}

// Test-only: drive the module-level refCount back to 0. Module state survives
// between tests in the same file (unlike vi.resetModules() + dynamic import), so
// a suite that mounts without fixture auto-teardown must call this in afterEach
// to keep the count honest. NOT part of the runtime API — do not call in app code.
export function __resetPendoRefCountForTests(): void {
  refCount = 0;
  darkRefCount = 0;
}
